// src/pages/Dashboard.jsx
import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  VStack,
  HStack,
  Icon,
} from "@chakra-ui/react";
import {
  FaSearch,
  FaUserFriends,
  FaUserPlus,
  FaSolarPanel,
  FaPlus,
  FaTools,
  FaFileAlt,
} from "react-icons/fa";

import { useColorMode } from "../components/ui/color-mode";
import AuthContext from "../context/AuthContext";

// Configurable dashboard sections
const SECTIONS = [
  {
    title: "SERVICEPARTNER-TOOL",
    items: [
      //{ label: "Suche", icon: FaSearch, path: "/suche" },   (comment the search icon)
      { label: "Servicepartner", icon: FaUserFriends, path: "/service-partner/liste" },
      { label: "Servicepartner anlegen", icon: FaUserPlus, path: "/service-partner/neu" },
    ],
  },
  {
    title: "WARTUNGS-TOOL",
    items: [
      //{ label: "Suche", icon: FaSearch, path: "/wartung/suche" },    (comment the search icon)
      { label: "PV-Anlage", icon: FaSolarPanel, path: "/anlagen" },
      { label: "PV-Anlage erstellen", icon: FaPlus, path: "/anlagen/neu" },
      { label: "Wartungsliste", icon: FaTools, path: "/wartung/liste" },
      { label: "Wartungsprotokoll", icon: FaFileAlt, path: "/wartung/wartungsprotokoll" },
    ],
  },
];

export default function Dashboard() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const { colorMode } = useColorMode();
  const username = user?.email?.split("@")[0] || "";

  const sectionTitleColor = colorMode === "light" ? "blue.500" : "blue.300";

  // Blue background, white text/icons
  const cardBg = "blue.500";
  const cardBorder = "blue.500";
  const iconColor = "white";
  const textColor = "white";

  // Hover: darker blue
  const hoverBg = "blue.600";
  const hoverTextColor = "white";
  const hoverIconColor = "white";

  const headerGradientLight = "linear(to-r, brand.500, brand.700)";
  const headerGradientDark = "linear(to-r, blue.500, blue.700)";
  const headerGradient =
    colorMode === "light" ? headerGradientLight : headerGradientDark;

  return (
    // FULL WIDTH light-blue background
    <Box bg="blue.50" w="100%" borderRadius="2xl" overflow="hidden">
      {/* Content stays centered */}
      <Container maxW="7xl" py={4}>
        {/* Welcome band */}
        <Box
          bgGradient={headerGradient}
          color="blue.500"
          py={{ base: 6, md: 12 }}
          mb={6}
          borderRadius="xl"
          boxShadow="md"
        >
          <Container maxW="7xl">
            <VStack align="start" spacing={2}>
              <Heading size="lg">Hallo {username.toUpperCase()}</Heading>
              <Text opacity={0.95}>
                Willkommen im ENVARIS Dashboard – Schnellzugriff auf Servicepartner und Wartung
              </Text>
            </VStack>
          </Container>
        </Box>

        <VStack align="stretch" spacing={10}>
          {SECTIONS.map(({ title, items }) => (
            <Box key={title} mb={10}>
              <Heading size="md" color={sectionTitleColor} mb={4}>
                {title}
              </Heading>

              {/* change the space from 6 to 9 to have more space between the (the buttons/cards) */}
              <SimpleGrid
                columns={{ base: 2, md: Math.min(items.length, 5) }}
                spacing={9}
                gap={9}
                p={title === "SERVICEPARTNER-TOOL" ? 3 : 5}
              >
                {items.map(({ label, icon, path }) => (
                  <DashboardCard
                    key={label}
                    label={label}
                    icon={icon}
                    onClick={() => navigate(path)}
                    cardBg={cardBg}
                    cardBorder={cardBorder}
                    iconColor={iconColor}
                    textColor={textColor}
                    hoverBg={hoverBg}
                    hoverTextColor={hoverTextColor}
                    hoverIconColor={hoverIconColor}
                  />
                ))}
              </SimpleGrid>
            </Box>
          ))}
        </VStack>
      </Container>
    </Box>
  );
}

// Reusable Dashboard Card
function DashboardCard({
  icon,
  label,
  onClick,
  cardBg,
  cardBorder,
  iconColor,
  textColor,
  hoverBg,
  hoverTextColor,
  hoverIconColor,
}) {
  const IconComp = icon;
  return (
    <Box
      role="button"
      onClick={onClick}
      cursor="pointer"
      bg={cardBg}
      color={textColor}
      borderWidth="1px"
      borderColor={cardBorder}
      borderRadius="2xl"
      boxShadow="sm"
      _hover={{
        boxShadow: "md",
        bg: hoverBg,
        color: hoverTextColor,
        transform: "translateY(-2px)",
        "& svg": { color: hoverIconColor },
      }}
      _focus={{
        boxShadow: "outline",
        bg: hoverBg,
        color: hoverTextColor,
        "& svg": { color: hoverIconColor },
      }}
      transition="all 0.15s ease"
      p={5}
    >
      <VStack spacing={3}>
        <HStack justify="center">
          <Icon
            as={IconComp}
            boxSize={10}
            color={iconColor}
            transition="color 0.15s ease"
          />
        </HStack>
        <Text fontWeight="medium" textAlign="center">
          {label}
        </Text>
      </VStack>
    </Box>
  );
}